import type { ImageAsset, JournalNote } from '@/types'

/** Left, centre and right hero portraits. The opening sequence flies onto these. */
export const heroImages: ImageAsset[] = [
    { src: '/images/lummi/hero-new-1.png', width: 1363, height: 1154, alt: "Aurelia in a black coat looking up in warm sunlight beside a stone column" },
    { src: '/images/lummi/hero-new-2.png', width: 1363, height: 1154, alt: "Aurelia gazing out of a car window at dusk with London lights behind her" },
    { src: '/images/lummi/hero-new-3.png', width: 1363, height: 1154, alt: "Aurelia sitting by a stone wall at sunset with the city skyline behind her" }
]

export const aboutTeaserImage: ImageAsset = {
    src: '/images/lummi/about-band.jpg',
    width: 2000,
    height: 1121,
    alt: "Black and white portrait of Aurelia with a dark bob, sitting in a quiet room under a round paper lamp"
}

export type Service = {
    number: string
    title: string
    description: string
    enquiryHref: string
    images: [ImageAsset, ImageAsset]
}

export const services: Service[] = [
    {
        number: '01',
        title: "Brand Identity",
        description: "Naming, logo, typography and a system that grows with you.",
        enquiryHref: "/contact?service=brand",
        images: [
            { src: '/images/lummi/service-brand-1.jpg', width: 900, height: 900, alt: "Minimal packaging design with clean labels on a soft background" },
            { src: '/images/lummi/service-brand-2.jpg', width: 900, height: 1200, alt: "Skincare bottles arranged as a calm brand display" }
        ]
    },
    {
        number: '02',
        title: "Web Design",
        description: "Websites with a point of view, designed page by page.",
        enquiryHref: "/contact?service=website",
        images: [
            { src: '/images/projects/web-design.png', width: 1460, height: 1078, alt: "Laptop showing a gallery website on a stone desk in afternoon light" },
            { src: '/images/design/stone-1.jpg', width: 1050, height: 1500, alt: "Hand holding a phone with an app open" }
        ]
    },
    {
        number: '03',
        title: "Creative Development",
        description: "Interactive builds with smooth motion and clean detail.",
        enquiryHref: "/contact?service=website",
        images: [
            { src: '/images/lummi/service-dev-1.jpg', width: 900, height: 1195, alt: "Surreal glass forms catching light" },
            { src: '/images/lummi/service-dev-2.jpg', width: 900, height: 1195, alt: "A glowing cube floating in a dark space" }
        ]
    },
    {
        number: '04',
        title: "Motion & Interaction",
        description: "Animation and micro-interactions that make interfaces feel alive.",
        enquiryHref: "/contact?service=motion",
        images: [
            { src: '/images/lummi/service-motion-1.jpg', width: 900, height: 1195, alt: "Ethereal streaks of motion blur" },
            { src: '/images/lummi/service-motion-2.jpg', width: 900, height: 1195, alt: "Abstract metallic shapes in motion" }
        ]
    },
    {
        number: '05',
        title: "Art Direction",
        description: "Visual direction for campaigns, launches and editorial work.",
        enquiryHref: "/contact?service=art-direction",
        images: [
            { src: '/images/design/service-art-1.jpg', width: 1208, height: 1500, alt: "Fabric swatches, pencils and paper laid out for an art direction board" },
            { src: '/images/design/grey-2.jpg', width: 818, height: 1500, alt: "Geometric campaign posters clipped to a white brick wall" }
        ]
    },
    {
        number: '06',
        title: "Product Design",
        description: "Clear, friendly interfaces for apps and digital tools.",
        enquiryHref: "/contact?service=product",
        images: [
            { src: '/images/projects/product.png', width: 1460, height: 1078, alt: "Phone showing a calm wellness app home screen on warm concrete" },
            { src: '/images/projects/kora-detail.jpg', width: 1500, height: 1000, alt: "Two hands passing a phone with an app glowing in warm pink" }
        ]
    }
]

export const processSteps = [
    {
        number: '01',
        title: "Hello",
        body: "A short call to understand your goals. Reply within a day."
    },
    {
        number: '02',
        title: "Concept",
        body: "Moodboards and first routes within a week."
    },
    {
        number: '03',
        title: "Design",
        body: "Focused rounds of feedback until it feels right."
    },
    {
        number: '04',
        title: "Launch",
        body: "Handover, guidelines and support after going live."
    }
]

export const marqueeImages: string[] = [
    '/images/marquee/portrait-01.jpeg',
    '/images/marquee/portrait-02.jpeg',
    '/images/marquee/portrait-03.webp',
    '/images/marquee/portrait-04.jpeg',
    '/images/marquee/portrait-05.jpeg',
    '/images/marquee/portrait-06.jpeg',
    '/images/marquee/portrait-07.jpeg',
    '/images/marquee/portrait-08.jpeg',
    '/images/marquee/portrait-09.jpeg',
    '/images/marquee/portrait-10.jpeg',
    '/images/marquee/portrait-11.jpeg',
    '/images/marquee/portrait-12.jpeg',
    '/images/marquee/portrait-13.jpeg',
    '/images/marquee/portrait-14.jpeg',
    '/images/marquee/portrait-15.jpeg'
]

export const testimonials = [
    {
        quote: "Aurelia understood our brand before we could explain it. The identity she created still feels fresh two years later.",
        name: "Nadia H.",
        role: "Founder, Halden"
    },
    {
        quote: "Precise, fast and genuinely thoughtful. The website felt like our gallery the moment it went live.",
        name: "Leo M.",
        role: "Creative Director, Oriel"
    },
    {
        quote: "She asks the right questions and then simplifies everything. Our app has never been easier to use.",
        name: "Priya D.",
        role: "Head of Product, Kōra"
    }
]

export const journalNotes: JournalNote[] = [
    {
        slug: 'why-restraint-wins',
        title: "Why Restraint Wins",
        tag: "Design",
        date: '2025-02-03',
        readingTime: "2 min read",
        image: { src: '/images/design/journal-1.jpg', width: 1125, height: 1500, alt: "Minimal desk with an open notebook, a pencil and a eucalyptus sprig" },
        intro: "The strongest design decision on most projects is the thing you choose to leave out.",
        body: [
            "Every project reaches a moment where adding feels like progress. Another colour for the secondary buttons, another typeface for the pull quotes, another animation for the footer. Each one is easy to defend on its own, and together they quietly bury the idea that made the work worth doing in the first place.",
            "Restraint is not minimalism for its own sake. It is the habit of asking what each element is for, and being honest when the answer is \"nothing much\". When I rebuilt the Maison Tevre identity, the old brand had four logos, three gold foils and a crest nobody could describe. We kept one letter. Everything else had to earn its place again.",
            "The result felt more confident, not less. When there is less on the page, the things that remain carry more weight: a single colour becomes a signature, one well-set headline does the work of a slogan, and white space starts to feel expensive rather than empty.",
            "Restraint also makes a system easier to live with. A brand with two typefaces and a short palette is one a small team can use without a forty page manual. It survives new hires, rushed deadlines and the inevitable last-minute social post.",
            "So before I add something, I try to remove something. Most of the time the work gets clearer, and the client never misses what went."
        ]
    },
    {
        slug: 'from-sketch-to-launch',
        title: "From Sketch to Launch",
        tag: "Process",
        date: '2024-10-17',
        readingTime: "2 min read",
        image: { src: '/images/design/journal-2.jpg', width: 1125, height: 1500, alt: "Logo sketches on a white desk beside gold glasses and a pen" },
        intro: "How a project moves from a page of scribbles to something live, without losing the idea along the way.",
        body: [
            "Every project starts in a sketchbook, usually before I open a laptop. Pencil is forgiving. It lets me try ten bad ideas in an hour without any of them looking finished, which is exactly the point. At this stage I am not designing screens, I am looking for the one sentence that describes how the work should feel.",
            "Once that sentence exists, the moodboard follows. References, colours, type specimens and a few rough layouts go into a single shared page, and the client sees it within a week. This is where we agree on direction, long before anyone argues about the size of a button.",
            "Design happens in focused rounds. I would rather show three considered screens than thirty hurried ones, and every round ends with a clear decision rather than a list of maybes. Feedback is easier to give when there is less to look at.",
            "When it is time to build, I write the front end myself or work side by side with the client's developers. Motion, spacing and responsive behaviour are designed in the browser, because that is where they actually live.",
            "Launch is not the end. The first weeks after going live teach you more than any review. I stay close, fix the small things and hand over guidelines so the work keeps its shape long after I have stepped away."
        ]
    },
    {
        slug: 'the-quiet-power-of-type',
        title: "The Quiet Power of Type",
        tag: "Typography",
        date: '2024-05-20',
        readingTime: "2 min read",
        image: { src: '/images/design/journal-3.jpg', width: 841, height: 1500, alt: "Close-up of interlocking wooden blocks in warm beige tones" },
        intro: "Most people never notice typography. That is usually a sign it is working.",
        body: [
            "Type is the voice of a brand before anyone reads a single word. A tall condensed sans feels urgent, a soft serif feels patient, and a generous line height feels like someone taking their time to explain. Visitors pick this up in a fraction of a second, long before they have decided whether they like you.",
            "I spend more time on type than on almost anything else. Choosing the family is only the start. The real work is in the details: the size steps between headings, the space between letters at large sizes, the length of a comfortable line, and the way numbers sit in a table.",
            "Good typography is mostly invisible. Nobody writes to say the line length on your About page was perfect. They simply read to the end, and that is the whole point.",
            "On the Fenwick and Dale identity, the firm's previous website used six weights of the same font. We cut it down to two, tightened the headlines and opened up the body copy. Nothing else changed in the first round, and the site already felt calmer and more trustworthy.",
            "If a brand only has the budget for one careful decision, I would always spend it on type. It shows up everywhere, it ages slowly, and it quietly holds everything else together."
        ]
    }
]

export const footerWordmarkImage = '/images/lummi/footer.jpg'

export const faqs = [
    {
        question: "What do you design?",
        answer: "Brand identities, websites and digital products. If it needs a clear idea and good taste, it is probably for me."
    },
    {
        question: "Where are you based?",
        answer: "East London, working with clients remotely across the UK, Europe and beyond."
    },
    {
        question: "Do you work with startups?",
        answer: "Yes, often. I enjoy helping young teams find a voice and look confident from day one."
    },
    {
        question: "How early should I book?",
        answer: "Four to six weeks ahead is ideal. Smaller projects can sometimes start sooner."
    },
    {
        question: "Do you build the website too?",
        answer: "I design and build the front end myself, or hand over polished files to your developers."
    },
    {
        question: "How long does a project take?",
        answer: "A brand identity takes about six weeks, a website four to eight, depending on size."
    },
    {
        question: "How do you charge?",
        answer: "Fixed project fees after a short scoping call, so there are no surprises."
    },
    {
        question: "How do I get started?",
        answer: "Send a message through the contact page with a few lines about what you have in mind."
    }
]

export const biography = {
    image: { src: '/images/lummi/journal-1.jpg', width: 1400, height: 1863, alt: "Portrait of Aurelia with red hair against a muted background" } as ImageAsset,
    paragraphs: [
        "Auren is my independent practice, based in a light-filled studio in east London. I work across identity, web and product, always starting with a simple question: what should this feel like?",
        "I began at a small branding studio sketching logos at night, and learned that good design is mostly clarity and patience.",
        "Today I take on a handful of projects a year, so each client gets my full attention, from first sketch to final detail."
    ],
    quote: "Clarity is the most generous thing a designer can give."
}

export const principles = [
    {
        title: "Start with the idea",
        body: "Before anything is drawn, I want to know what is worth saying."
    },
    {
        title: "Keep it human",
        body: "Good design feels warm, clear and easy to live with."
    },
    {
        title: "Finish with care",
        body: "Every pixel, margin and transition gets looked at twice."
    }
]

export const journey = [
    { years: "2020 to now", role: "Independent Designer & Creative Director", studio: "Auren", detail: "Brands, websites and products for a small circle of clients, with every project led and made by me." },
    { years: "2018 to 2020", role: "Senior Designer", studio: "Northlight Studio", detail: "Led identity and web projects for cultural clients and learned to run a room full of opinions." },
    { years: "2016 to 2018", role: "Brand Designer", studio: "Pell Street", detail: "Built brand systems for food, fashion and hospitality, and fell for typography for good." },
    { years: "2013 to 2016", role: "Junior Designer", studio: "Fold & Press", detail: "Print, packaging and endless type specimens at a small independent press." },
    { years: "2011 to 2013", role: "Design Intern", studio: "Esh Studio", detail: "Sketching logos at night, tidying files by day and learning what good feedback sounds like." }
]

export type KitGroup = {
    group: string
    items: Array<{ name: string, note: string, why: string }>
}

export const kit: [KitGroup[], KitGroup[]] = [
    [
        {
            group: "Thinking",
            items: [
                { name: "Sketchbooks", note: "Pencil and paper", why: "Ideas stay loose on paper, so I can throw away ten of them without feeling attached." },
                { name: "Type foundries", note: "Licensed type", why: "The right typeface sets the voice of a brand before anyone reads a word." },
                { name: "A worn-out set of Pantone books", note: "Colour guides", why: "Screens lie about colour. Paper swatches keep every palette honest from screen to print." }
            ]
        }
    ],
    [
        {
            group: "Making",
            items: [
                { name: "Figma", note: "Interface design", why: "Where layouts, design systems and client reviews all live in one shared place." },
                { name: "Blender", note: "3D", why: "For objects, light and textures that would cost a fortune to build for real." },
                { name: "After Effects", note: "Motion", why: "Every transition gets timed and tested here before it is built for the web." }
            ]
        }
    ]
]

export const studioTour = {
    image: {
        src: '/images/design/studio-wide.jpg',
        width: 1500,
        height: 844,
        alt: "The Auren studio: a cork board of brand sketches and colour tests above a white desk with a laptop, phone and drawing tablet, beside a bright window"
    } as ImageAsset,
    hotspots: [
        {
            id: 'second-screen',
            label: "Test screens",
            body: "A laptop and a phone side by side, so every build gets checked on a real screen before it ships.",
            x: 57,
            y: 62
        },
        {
            id: 'sketch-wall',
            label: "The sketch wall",
            body: "A cork board covered in pinned sketches, type tests and colour studies, so every idea stays in sight.",
            x: 28,
            y: 22
        },
        {
            id: 'reference-shelf',
            label: "Reference shelf",
            body: "Books, printed matter and samples I keep coming back to, from old type specimens to fabric swatches.",
            x: 12,
            y: 72
        },
        {
            id: 'coffee-corner',
            label: "Window corner",
            body: "A bright window lined with plants, where client calls and first reviews happen.",
            x: 87,
            y: 38
        }
    ]
}

export const awards = [
    { year: '2025', award: "Brand of the Year", organization: "Digital Craft Awards" },
    { year: '2025', award: "Studio to Watch", organization: "London Design Guild" },
    { year: '2024', award: "Interactive Site of the Year", organization: "Web Excellence Council" },
    { year: '2024', award: "Rising Designer", organization: "Frame International" },
    { year: '2024', award: "Best Typography", organization: "Print & Pixel Awards" },
    { year: '2024', award: "Best Portfolio", organization: "UK Designers Association" },
    { year: '2023', award: "Storytelling Prize", organization: "Northern Lights Awards" },
    { year: '2022', award: "Emerging Talent", organization: "London Arts Network" },
    { year: '2021', award: "Identity Excellence", organization: "World Design Forum" },
    { year: '2020', award: "Designer to Watch", organization: "European Creative Awards" }
]

export const clients = ['Halden', 'Oriel', 'Maison Tevre', 'Kōra', 'Fenwick & Dale', 'Lumen Row']

export const workCtaImages: ImageAsset[] = [
    { src: '/images/design/about-hero-2.png', width: 1130, height: 1500, alt: "Hands typing on a laptop at a clean black and white desk" },
    { src: '/images/design/vows-1.jpg', width: 1208, height: 1500, alt: "Stationery flat lay with serif type and a wax seal" }
]

export const aboutHeroImage: ImageAsset = {
    src: '/images/design/biography.png',
    width: 1500,
    height: 750,
    alt: "Brand sketches and colour swatches spread across a designer's desk"
}

export const nextSteps = [
    { number: '01', title: "We talk" },
    { number: '02', title: "You get a proposal" },
    { number: '03', title: "We build it together" }
]
